import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collectionService, usersService, settingsService } from "@/services";
import type { ReceiptView, StaffUser, BusinessSettings } from "@/services/types";
import { useAuth } from "@/context/AuthContext";
import { roleLabel, canReverseCollection } from "@/config/roles";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { Card } from "@/components/base/Card";
import { Button } from "@/components/base/Button";
import { ErrorState } from "@/components/base/ErrorState";
import { Alert } from "@/components/base/Alert";
import { PrintLayout } from "@/components/feature/PrintLayout";
import { formatINRNumber, formatDate, formatDateTime } from "@/utils/format";
import { paymentModeLabel } from "../components/labels";
import { useBranding } from "@/context/BrandingContext";
import { ReversePaymentModal } from "../components/ReversePaymentModal";

export default function ReceiptPage() {
  const { receiptId } = useParams<{ receiptId: string }>();
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const canReverse = canReverseCollection(role);
  const { businessName, effectiveLogo } = useBranding();
  const [businessSettings, setBusinessSettings] = useState<BusinessSettings | null>(null);
  const [receipt, setReceipt] = useState<ReceiptView | null>(null);
  const [staffUsers, setStaffUsers] = useState<StaffUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [slot, setSlot] = useState<"top" | "middle" | "bottom" | "all">("top");
  const [reversalOpen, setReversalOpen] = useState(false);

  useEffect(() => {
    settingsService.getBusinessSettings().then(setBusinessSettings).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!receiptId) return;
    setError(null);
    try {
      const [r, u] = await Promise.all([
        collectionService.getReceipt(receiptId),
        usersService.listUsers().catch(() => [] as StaffUser[]),
      ]);
      setReceipt(r);
      setStaffUsers(u);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load this receipt.");
      setReceipt(null);
    }
  }, [receiptId]);

  useEffect(() => {
    load();
  }, [load]);

  const back = (
    <Button variant="outline" size="sm" icon={<i className="ri-arrow-left-line" />} onClick={() => navigate(-1)}>
      Back
    </Button>
  );

  const breadcrumb = receipt ? (
    <>
      <BreadcrumbItem label="Home" to="/" />
      <BreadcrumbItem label="Collection" to="/collection" />
      <BreadcrumbItem label={`Receipt ${receipt.receiptNumber}`} isLast />
    </>
  ) : (
    <>
      <BreadcrumbItem label="Home" to="/" />
      <BreadcrumbItem label="Collection" to="/collection" />
      <BreadcrumbItem label="Receipt" isLast />
    </>
  );

  if (error) {
    return (
      <div>
        <PageHeader breadcrumb={breadcrumb} title="Receipt" actions={back} />
        <Card><ErrorState message={error} onRetry={load} /></Card>
      </div>
    );
  }

  if (!receipt) {
    return (
      <div>
        <PageHeader breadcrumb={breadcrumb} title="Loading receipt…" actions={back} />
        <div className="h-72 animate-pulse rounded-lg bg-background-200" />
      </div>
    );
  }

  // Real user lookup to get true name and real designation
  const rawCollected = (receipt.collectedBy || "").trim();
  const matchedUser = staffUsers.find(
    (u) =>
      u.name.toLowerCase() === rawCollected.toLowerCase() ||
      u.id === rawCollected ||
      u.email.toLowerCase() === rawCollected.toLowerCase()
  );

  const collectorName = matchedUser?.name || (rawCollected && rawCollected !== "Staff" && rawCollected !== "Staff User" ? rawCollected : user?.name || "Super Admin");
  const collectorDesignation = matchedUser?.role
    ? roleLabel(matchedUser.role)
    : user && user.name.toLowerCase() === collectorName.toLowerCase() && user.role
    ? roleLabel(user.role)
    : user?.role
    ? roleLabel(user.role)
    : "";

  const collectorText = collectorDesignation
    ? `${collectorName}(${collectorDesignation})`
    : collectorName;

  return (
    <div>
      <PageHeader
        className="no-print"
        breadcrumb={breadcrumb}
        title={receipt.receiptNumber}
        subtitle={`Issued ${formatDateTime(receipt.createdAt)} by ${receipt.collectedBy}`}
        actions={
          <div className="flex items-center gap-2">
            {canReverse && !receipt.reversedAt ? (
              <Button
                variant="danger"
                size="sm"
                icon={<i className="ri-arrow-go-back-line" />}
                onClick={() => setReversalOpen(true)}
              >
                Revert / Delete Transaction
              </Button>
            ) : null}
            {back}
          </div>
        }
      />
      {receipt.reversedAt ? (
        <div className="mb-3 no-print">
          <Alert variant="error" title="Reversed receipt">
            Reversed on {formatDate(receipt.reversedAt)} by {receipt.reversedBy}. Reason: {receipt.reversalReason}. This
            receipt is not counted toward any balance.
          </Alert>
        </div>
      ) : null}

      <PrintLayout
        title="Payment Receipt"
        meta={`Receipt ${receipt.receiptNumber} · ${formatDate(receipt.receivedOn)} (1/3 of A4)`}
        actions={
          <div className="flex items-center gap-2">
            {receipt.reversedAt ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-danger-600">
                <i className="ri-close-circle-line" /> Reversed
              </span>
            ) : null}
            <div className="no-print flex items-center gap-1 text-xs">
              <span className="text-foreground-500">A4 Print Layout:</span>
              <div className="flex items-center rounded-md border border-background-200 bg-background-100 p-0.5">
                <button
                  type="button"
                  title="Print on top 1/3 slot of A4"
                  className={`cursor-pointer rounded px-2 py-1 font-medium transition-colors ${
                    slot === "top" ? "bg-white font-semibold text-foreground-900 shadow-sm" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                  onClick={() => setSlot("top")}
                >
                  Top (1/2)
                </button>
                <button
                  type="button"
                  title="Print on middle 1/3 slot"
                  className={`cursor-pointer rounded px-2 py-1 font-medium transition-colors ${
                    slot === "middle" ? "bg-white font-semibold text-foreground-900 shadow-sm" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                  onClick={() => setSlot("middle")}
                >
                  Middle (1/2)
                </button>
                <button
                  type="button"
                  title="Print on bottom 1/3 slot"
                  className={`cursor-pointer rounded px-2 py-1 font-medium transition-colors ${
                    slot === "bottom" ? "bg-white font-semibold text-foreground-900 shadow-sm" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                  onClick={() => setSlot("bottom")}
                >
                  Bottom (1/2)
                </button>
                <button
                  type="button"
                  title="Print all 3 receipts on full A4 page"
                  className={`cursor-pointer rounded px-2 py-1 font-medium transition-colors ${
                    slot === "all" ? "bg-white font-semibold text-foreground-900 shadow-sm" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                  onClick={() => setSlot("all")}
                >
                  All 2 on A4
                </button>
              </div>
            </div>
          </div>
        }
      >
        {/* A4 Sheet Container */}
        <div className="receipt-a4-sheet mx-auto w-full max-w-[210mm] print:m-0 print:w-full print:max-w-none">
          {slot === "all" ? (
            <div className="flex flex-col justify-between space-y-2 print:h-[285mm] print:space-y-0">
              <ReceiptSlip receipt={receipt} collectorText={collectorText} businessSettings={businessSettings} />
              <CutLine />
              <ReceiptSlip receipt={receipt} collectorText={collectorText} businessSettings={businessSettings} />
              <CutLine />
              <ReceiptSlip receipt={receipt} collectorText={collectorText} businessSettings={businessSettings} />
            </div>
          ) : (
            <>
              {/* Top spacer if printing on middle or bottom slot of re-fed A4 sheet */}
              {slot === "middle" ? <div className="slot-spacer h-[95mm]" /> : null}
              {slot === "bottom" ? <div className="slot-spacer h-[190mm]" /> : null}

              {/* Single Receipt Slip (occupies exact 1/3 of A4) */}
              {/* Single Receipt Slip */}
              <ReceiptSlip receipt={receipt} collectorText={collectorText} businessSettings={businessSettings} />

              {/* Guide Cut-Line & Remaining Blank Area on screen */}
              <div className="no-print mt-4 space-y-2">
                <CutLine />
                <div className="rounded border border-dashed border-background-300 bg-background-50/50 p-6 text-center text-xs text-foreground-400">
                  Remaining {slot === "top" ? "2/3" : slot === "middle" ? "1/2" : "part"} of A4 sheet is left blank for subsequent receipts or cutting.
                </div>
              </div>
            </>
          )}
        </div>
      </PrintLayout>

      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 5mm 6mm;
          }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
          }
          .receipt-a4-sheet {
            display: block !important;
            width: 100% !important;
            height: auto !important;
            max-height: none !important;
          }
          .slot-spacer {
            display: block !important;
          }
          .mk-receipt-box {
            border: 2px solid #000 !important;
            box-shadow: none !important;
            padding: 5mm 6mm !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            height: auto !important;
            min-height: auto !important;
            max-height: none !important;
            margin-bottom: 0 !important;
            box-sizing: border-box !important;
          }
          .cut-line {
            margin: 2mm 0 !important;
          }
        }
      `}</style>

      {/* Delete / Revert Receipt Modal */}
      <ReversePaymentModal
        open={reversalOpen}
        target={
          receipt
            ? {
                id: receipt.id,
                receiptNumber: receipt.receiptNumber,
                memberName: receipt.memberName,
                memberCode: receipt.memberCode,
                amount: receipt.total,
                mode: receipt.mode,
                date: receipt.createdAt || receipt.receivedOn,
                installmentInfo: receipt.lines.map((l) => `${l.groupName} (${l.cycle})`).join(", "),
              }
            : null
        }
        onClose={() => setReversalOpen(false)}
        onSuccess={() => load()}
      />
    </div>
  );
}

function CutLine() {
  return (
    <div className="cut-line flex items-center justify-center gap-2 py-1 text-xs text-foreground-400 print:text-[9.5px] print:text-foreground-500">
      <span className="text-sm">✂</span>
      <span className="flex-1 border-b border-dashed border-foreground-400"></span>
      <span className="uppercase tracking-widest font-medium">Cut Along Line</span>
      <span className="flex-1 border-b border-dashed border-foreground-400"></span>
    </div>
  );
}

function ReceiptSlip({
  receipt,
  collectorText,
  businessSettings,
}: {
  receipt: ReceiptView;
  collectorText: string;
  businessSettings: BusinessSettings | null;
}) {
  const { businessName, effectiveLogo } = useBranding();
  const firstLine = receipt.lines[0];
  const cycleNumber = firstLine?.cycle ?? 1;
  const entryNumber = receipt.ticketNo ?? firstLine?.ticketNo ?? "—";
  const totalMembers = receipt.totalMembers ?? (receipt.chitValue && receipt.groupInstallmentAmount ? Math.round(receipt.chitValue / receipt.groupInstallmentAmount) : undefined);

  // Compute per-member firm's commission safely
  const perMemberCommission = (() => {
    if (!firstLine || firstLine.commissionAmount == null || firstLine.commissionAmount === 0) return 0;
    const count = totalMembers ?? 1;
    const base = firstLine.baseAmount ?? receipt.groupInstallmentAmount ?? receipt.total;
    const dividend = firstLine.dividendCredit ?? 0;
    const late = Math.max(0, firstLine.lateFee ?? 0);

    // Check if base - dividend + late + firstLine.commissionAmount matches receipt.total
    const directlyMatches = Math.abs((base - dividend + late + firstLine.commissionAmount) - receipt.total) < 0.05;
    if (directlyMatches) return firstLine.commissionAmount;

    // If commissionAmount was total cycle commission, divide by totalMembers
    if (count > 1) {
      const perMember = Math.floor((firstLine.commissionAmount * 100) / count) / 100;
      const dividedMatches = Math.abs((base - dividend + late + perMember) - receipt.total) < 0.05;
      if (dividedMatches) return perMember;
    }
    return firstLine.commissionAmount;
  })();

  return (
    <div className="mk-receipt-box mx-auto flex w-full flex-col justify-between border-2 border-foreground-950 bg-white p-4 font-sans text-xs leading-normal text-foreground-950 print:border-[2px] print:p-4 print:text-[11.5px]">
      {receipt.reversedAt ? (
        <div className="mb-2 rounded border-2 border-dashed border-red-600 bg-red-50 p-2 text-center text-xs font-bold text-red-700 print:mb-2 print:border-red-600 print:bg-red-50 print:p-1.5 print:text-[11px] print:text-red-700">
          ⚠️ THIS RECEIPT HAS BEEN VOIDED / REVERSED {receipt.reversedAt ? `ON ${formatDate(receipt.reversedAt)}` : ""}
          {receipt.reversalReason ? ` — REASON: "${receipt.reversalReason}"` : ""}
        </div>
      ) : null}

      {/* Top Regd / GSTN Header */}
      <div className="flex items-center justify-between border-b border-foreground-950/20 pb-1.5 text-xs font-semibold text-foreground-800 print:pb-1.5 print:text-[11px]">
        <span>Regd. No.:- DL-11-0155242</span>
        <div className="flex items-center gap-2">
          <span
            className={`rounded border px-3 py-0.5 text-xs font-bold tracking-widest print:text-[11.5px] ${
              receipt.reversedAt
                ? "border-red-600 bg-red-100 text-red-700 print:border-red-600 print:bg-red-100 print:text-red-700"
                : "border-foreground-950 text-foreground-950"
            }`}
          >
            {receipt.reversedAt ? "VOID / REVERSED" : "RECEIPT"}
          </span>
        </div>
        <span>{businessSettings?.registrationNumber || "GSTN. NO. 07ARYPK6255G1Z7"}</span>
      </div>

      {/* Main Company Branding Header */}
      <div className="flex items-center justify-between gap-2 py-2 print:py-2">
        {/* Logo on Left */}
        <div className="flex-none">
          <img
            src={effectiveLogo}
            alt={businessName}
            className="h-12 max-h-12 w-auto max-w-[54px] object-contain print:h-11 print:max-h-11 rounded"
            style={{ maxHeight: "48px" }}
          />
        </div>

        {/* Title & Addresses Centered */}
        <div className="flex-1 text-center min-w-0">
          <h1 className="text-xl sm:text-2xl font-black uppercase tracking-wider text-[#3b2d6a] mb-1 print:mb-1.5 print:text-[24px] print:font-black print:leading-tight">
            {businessName || "M.K. FINANCIAL SERVICES (REGD.)"}
          </h1>
          <p className="whitespace-nowrap text-[10.5px] font-medium text-foreground-800 leading-normal tracking-tight mb-1 print:mb-1 print:text-[10px] print:tracking-tight">
            Regd. Office :- {businessSettings?.address ? `${businessSettings.address}, ${businessSettings.city} ${businessSettings.state} - ${businessSettings.pinCode}` : "D - 58, GALI NO.5, NEAR PANDEY STORE, RAJAPURI, UTTAM NAGAR, WEST DELHI - 110059(INDIA)"}
          </p>
          <div className="mt-1 flex items-center justify-between text-[11px] font-medium text-foreground-700 print:mt-1 print:text-[10px]">
            <span>E-Mail ID :- {businessSettings?.email || "mkfinancialservices333@gmail.com"}</span>
            <span>Mob. No. : {businessSettings?.primaryPhone ? [businessSettings.primaryPhone, businessSettings.alternatePhone].filter(Boolean).join(", ") : "9811498128, 9555577732"}</span>
          </div>
        </div>

        {/* Empty balance spacer to keep header centered */}
        <div className="w-12 flex-none print:w-11" />
      </div>

      {/* 4-Column Metadata Row with Outer and Inner Borders */}
      <div className="grid grid-cols-4 border-y border-foreground-950 text-xs font-medium print:text-[11px]">
        <div className="border-r border-foreground-950 px-2.5 py-1.5 print:py-1.5">
          <span>Receipt No. :- </span>
          <strong className="tnum font-bold text-foreground-950">{receipt.receiptNumber}</strong>
        </div>
        <div className="border-r border-foreground-950 px-2.5 py-1.5 print:py-1.5">
          <span>MKFS:- </span>
          <strong className="tnum font-bold text-foreground-950">{firstLine?.groupCode || receipt.memberCode || "0007--"}</strong>
        </div>
        <div className="border-r border-foreground-950 px-2.5 py-1.5 print:py-1.5">
          <span>Start Date:- </span>
          <strong className="tnum font-bold text-foreground-950">{formatDate(receipt.startDate || receipt.receivedOn)}</strong>
        </div>
        <div className="px-2.5 py-1.5 print:py-1.5">
          <span>Receipt Date :- </span>
          <strong className="tnum font-bold text-foreground-950">{formatDate(receipt.receivedOn)}</strong>
        </div>
      </div>

      {/* Main 3-Column Body Section */}
      <div className="grid grid-cols-12 border-b border-foreground-950">
        {/* Column 1: Details of Subscribers */}
        <div className="col-span-5 flex flex-col justify-between border-r border-foreground-950 p-3 print:p-2.5">
          <div className="space-y-2.5 print:space-y-2.5">
            <p className="text-xs font-bold underline print:text-[11px]">Details of Subscribers :-</p>
            <div className="grid grid-cols-3 gap-1 pt-0.5 text-xs print:text-[11px]">
              <span className="font-semibold">NAME :</span>
              <span className="col-span-2 font-bold uppercase">{receipt.memberName}</span>
            </div>
            <div className="grid grid-cols-3 gap-1 text-xs print:text-[11px]">
              <span className="font-semibold">MOB. NO. :</span>
              <span className="tnum col-span-2 font-medium">{receipt.memberPhone}</span>
            </div>
            <div className="grid grid-cols-3 gap-1 text-xs print:text-[11px]">
              <span className="font-semibold">Ref. By :- </span>
              <span className="col-span-2 font-medium">
                {receipt.fatherOrSpouse ? `${receipt.fatherOrSpouse}` : (receipt.reference || "—")}
              </span>
            </div>
          </div>
          {/* Entry No. below details of subscribers */}
          <div className="border-t border-foreground-950/20 pt-1.5 text-[11.5px] font-semibold text-foreground-800 print:pt-1.5 print:text-[10.5px]">
            <span>Entry No. :- </span>
            <strong className="tnum font-bold text-foreground-950">{entryNumber}</strong>
          </div>
        </div>

        {/* Column 2: Cycle & Total Members */}
        <div className="col-span-2 flex flex-col justify-between border-r border-foreground-950 p-3 print:p-2.5">
          <div className="space-y-3 print:space-y-3">
            <div>
              <p className="text-[11.5px] font-semibold text-foreground-800 print:text-[10.5px]">Cycle No. :-</p>
              <p className="tnum mt-1 text-base font-extrabold leading-tight text-foreground-950 print:text-sm">{cycleNumber}</p>
            </div>
            <div>
              <p className="text-[11.5px] font-semibold text-foreground-800 print:text-[10.5px]">Total Members :-</p>
              <p className="tnum mt-1 text-sm font-bold leading-tight text-foreground-950 print:text-xs">{totalMembers ?? "—"}</p>
            </div>
          </div>
          <p className="tnum mt-1 truncate text-[11px] font-medium text-foreground-600 print:text-[10px]">
            {firstLine ? firstLine.groupName : (receipt.groupName || "")}
          </p>
        </div>

        {/* Column 3: In Rupees Financial Table */}
        <div className="col-span-5 p-3 print:p-2.5">
          <p className="mb-1 text-right text-[11px] font-semibold uppercase tracking-wider text-foreground-700 print:text-[10px]">
            ( IN RUPEES )
          </p>
          <div className="space-y-1.5 text-xs print:space-y-1.5 print:text-[11px]">
            <div className="flex justify-between">
              <span>Instalment Amount</span>
              <span className="tnum font-medium">
                {formatINRNumber(firstLine?.baseAmount ?? receipt.groupInstallmentAmount ?? receipt.total)}
              </span>
            </div>
            <div className="flex justify-between text-foreground-700">
              <span>Profit</span>
              <span className="tnum font-medium">
                {firstLine?.dividendCredit && firstLine.dividendCredit > 0
                  ? `- ${formatINRNumber(firstLine.dividendCredit)}`
                  : "—"}
              </span>
            </div>
            <div className="flex justify-between text-foreground-700">
              <span>Late Fee</span>
              <span className="tnum font-medium">
                {firstLine?.lateFee && firstLine.lateFee > 0
                  ? `+ ${formatINRNumber(firstLine.lateFee)}`
                  : "—"}
              </span>
            </div>
            <div className="flex justify-between text-foreground-700">
              <span>Firm&apos;s</span>
              <span className="tnum font-medium">
                {perMemberCommission && perMemberCommission > 0
                  ? `+ ${formatINRNumber(perMemberCommission)}`
                  : "—"}
              </span>
            </div>
          </div>
          {/* Total Row & Amount Deposit Row */}
          <div className="mt-2 border-t border-foreground-950 pt-1.5 text-xs print:mt-2 print:pt-1.5 print:text-[11px]">
            <div className="flex justify-between font-bold">
              <span>Total</span>
              <span className="tnum font-black">{formatINRNumber(receipt.total)}</span>
            </div>
            <div className="flex justify-between pt-1 font-semibold print:pt-1">
              <span>Amount Deposit</span>
              <span className="tnum font-bold">{formatINRNumber(receipt.total)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Balance Amount Row */}
      <div className="flex justify-end border-b border-foreground-950 px-3 py-1.5 text-xs font-bold print:py-1.5 print:text-[11.5px]">
        <div className="flex w-5/12 justify-between">
          <span>Balance Amount</span>
          <span className="tnum font-extrabold">{formatINRNumber(receipt.remainingBalance)}</span>
        </div>
      </div>

      {/* Bottom Scheme Details & Authorized Signature Row */}
      <div className="grid grid-cols-12 pt-3 text-xs print:pt-3 print:text-[11px]">
        {/* Left 2 sub-columns: Scheme details */}
        <div className="col-span-7 flex flex-col justify-between space-y-2 border-r border-foreground-950/20 pr-3 print:space-y-2">
          <div className="flex justify-between">
            <div>
              <span className="font-semibold">Value Scheme: </span>
              <span className="tnum font-bold">
                {receipt.chitValue
                  ? formatINRNumber(receipt.chitValue)
                  : receipt.groupInstallmentAmount && totalMembers
                  ? formatINRNumber(receipt.groupInstallmentAmount * totalMembers)
                  : formatINRNumber(receipt.total * 10)}
              </span>
            </div>
            <div>
              <span className="font-semibold">Loss Amount :- </span>
              <span className="tnum font-medium">
                {firstLine?.discountAmount != null ? formatINRNumber(firstLine.discountAmount) : "0.00"}
              </span>
            </div>
          </div>
          <div className="flex justify-between">
            <div>
              <span className="font-semibold">Inst. Amount:- </span>
              <span className="tnum font-bold">
                {formatINRNumber(firstLine?.baseAmount ?? receipt.groupInstallmentAmount ?? receipt.total)}
              </span>
            </div>
          </div>
          {/* ONLY ONE Single place for Collected By */}
          <div className="flex flex-wrap items-center justify-between gap-1 pt-1">
            <div>
              <span className="font-semibold">Details of Payment :- </span>
              <span className="font-bold uppercase text-foreground-950">
                {paymentModeLabel(receipt.mode)}
              </span>
              {receipt.reference ? (
                <span className="tnum ml-1 text-[10px] text-foreground-600">({receipt.reference})</span>
              ) : null}
            </div>
            <div>
              <span className="font-semibold">Collected By - </span>
              <span className="font-bold text-foreground-950">{collectorText}</span>
            </div>
          </div>
        </div>

        {/* Right signature column */}
        <div className="col-span-5 flex flex-col justify-between pl-3 text-right">
          <p className="text-xs font-bold tracking-tight text-foreground-950 print:text-[11px]">
            FOR M.K. FINANCIAL SERVICES ( REGD.)
          </p>
          <div className="pt-4 print:pt-3">
            <p className="text-xs font-bold text-foreground-900 print:text-[11px]">PROP. / AUTH. SIGN.</p>
          </div>
        </div>
      </div>
    </div>
  );
}